<template>
	<div class="relative">
		<div class="overflow-hidden bg-white border border-gray-200 shadow-lg rounded-xl">
			<!-- Report Header -->
			<div class="p-6 text-white bg-gradient-to-r from-blue-500 to-blue-600">
				<div class="flex items-center justify-between">
					<div>
						<h3 class="text-xl font-semibold">
							{{ sampleReport.childName }}
						</h3>
						<p class="text-blue-100">{{ sampleReport.age }} Jahre • {{ sampleReport.testDate }}</p>
					</div>
					<div class="text-right">
						<div class="text-2xl font-bold">
							{{ sampleReport.primaryType }}
						</div>
						<div class="text-sm text-blue-100">Hauptlerntyp</div>
					</div>
				</div>
			</div>

			<!-- Learning Type Chart -->
			<div class="p-6">
				<h4 class="mb-4 font-semibold">Lerntyp-Verteilung</h4>
				<div class="space-y-3">
					<div
						v-for="type in sampleReport.learningTypes"
						:key="type.name"
						class="flex items-center justify-between"
					>
						<span class="text-sm font-medium">{{ type.name }}</span>
						<div class="flex items-center space-x-2">
							<div class="w-24 h-2 bg-gray-200 rounded-full">
								<div
									:class="type.color"
									class="h-2 transition-all duration-500 rounded-full"
									:style="{ width: type.percentage + '%' }"
								/>
							</div>
							<span class="w-8 text-sm text-gray-600">{{ type.percentage }}%</span>
						</div>
					</div>
				</div>
			</div>

			<!-- Key Insights -->
			<div class="px-6 pb-6">
				<h4 class="mb-4 font-semibold">Wichtigste Erkenntnisse</h4>
				<div class="space-y-3">
					<div v-for="insight in sampleReport.insights" :key="insight" class="flex items-start">
						<div
							class="flex-shrink-0 w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5"
						>
							<span class="text-xs text-green-600">✓</span>
						</div>
						<span class="text-sm text-gray-700">{{ insight }}</span>
					</div>
				</div>
			</div>

			<!-- Recommendations Preview -->
			<div class="px-6 pb-6 border-t bg-gray-50">
				<h4 class="mb-3 font-semibold">Empfehlungen (Auszug)</h4>
				<div class="space-y-2">
					<div
						v-for="rec in sampleReport.recommendations"
						:key="rec"
						class="flex items-start text-sm text-gray-600"
					>
						<span class="flex-shrink-0 w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 mr-3" />
						{{ rec }}
					</div>
				</div>
				<div class="p-3 mt-4 rounded-lg bg-blue-50">
					<p class="text-sm font-medium text-blue-700">
						+ {{ additionalRecommendations }} weitere personalisierte Empfehlungen
					</p>
				</div>
			</div>
		</div>

		<!-- Floating Badge -->
		<div class="absolute -top-4 -right-4">
			<div class="px-4 py-2 text-sm font-semibold text-white bg-green-500 rounded-lg shadow-lg">
				Sofort verfügbar!
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
interface LearningType {
	name: string;
	percentage: number;
	color: string;
}

interface SampleReport {
	childName: string;
	age: number;
	testDate: string;
	primaryType: string;
	learningTypes: LearningType[];
	insights: string[];
	recommendations: string[];
}

interface Props {
	sampleReport?: SampleReport;
	additionalRecommendations?: number;
}

withDefaults(defineProps<Props>(), {
	sampleReport: () => ({
		childName: 'Emma',
		age: 8,
		testDate: 'Heute',
		primaryType: 'Visuell',
		learningTypes: [
			{ name: 'Visuell', percentage: 75, color: 'bg-blue-500' },
			{ name: 'Auditiv', percentage: 45, color: 'bg-green-500' },
			{ name: 'Kinästhetisch', percentage: 60, color: 'bg-purple-500' },
			{ name: 'Lesen/Schreiben', percentage: 55, color: 'bg-orange-500' },
		],
		insights: [
			'Emma lernt am besten durch Bilder und Grafiken',
			'Bewegung beim Lernen kann sehr hilfreich sein',
			'Strukturierte Lernumgebung ist wichtig',
		],
		recommendations: [
			'Nutze Mindmaps und visuelle Notizen',
			'Mache regelmäßige Pausen mit Bewegung',
			'Schaffe einen ruhigen Lernplatz',
		],
	}),
	additionalRecommendations: 25,
});
</script>
